// @ts-nocheck
import React, { useEffect, useState } from "react";
import {
  createParentRightsRequest,
  fetchParentRightsRequests,
  fetchParentStudents,
} from "../../services/api";

const TYPE_OPTIONS = [
  { value: "inspect", label: "Review my child's records" },
  { value: "amendment", label: "Ask the school to correct a record" },
  { value: "consent_grant", label: "Give consent to share records" },
  { value: "consent_revoke", label: "Withdraw consent to share records" },
];

const TYPE_LABELS = Object.fromEntries(TYPE_OPTIONS.map((opt) => [opt.value, opt.label]));

const STATUS_LABELS = {
  submitted: "Submitted — school will review",
  in_progress: "In progress at the school",
  fulfilled: "Completed (records made available)",
  approved: "Approved by the school",
  denied: "Denied by the school",
  closed: "Closed",
};

const pageStyle = { maxWidth: 720, margin: "0 auto", padding: "24px 16px 48px" };
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};
const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 };
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 14,
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function RightsRequests() {
  const [students, setStudents] = useState<any>([]);
  const [requests, setRequests] = useState<any>([]);
  const [studentId, setStudentId] = useState<any>("");
  const [requestType, setRequestType] = useState("inspect");
  const [requestDetail, setRequestDetail] = useState<any>("");
  const [loading, setLoading] = useState<any>(true);
  const [submitting, setSubmitting] = useState<any>(false);
  const [error, setError] = useState<any>("");
  const [success, setSuccess] = useState<any>("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [studentData, requestData] = await Promise.all([
        fetchParentStudents(),
        fetchParentRightsRequests(),
      ]);
      const list = studentData.students || [];
      setStudents(list);
      setRequests(requestData.requests || []);
      if (!studentId && list.length === 1) {
        setStudentId(String(list[0].student_id));
      }
    } catch (err: any) {
      setError(err.message || "Could not load your requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");
    if (!studentId) {
      setError("Please choose which child this request is about.");
      return;
    }
    setSubmitting(true);
    try {
      await createParentRightsRequest({
        studentId: Number(studentId),
        requestType,
        requestDetail,
      });
      setRequestDetail("");
      setSuccess("Request sent. Your school will review it — Academic Eye does not decide the outcome.");
      const refreshed = await fetchParentRightsRequests();
      setRequests(refreshed.requests || []);
    } catch (err: any) {
      setError(err.message || "Could not submit the request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <h1 style={{ margin: "0 0 8px", color: "#1e3a5f", fontSize: 26 }}>Record requests</h1>
      <p style={{ margin: "0 0 20px", color: "#64748b", lineHeight: 1.5, fontSize: 15 }}>
        Ask your child&apos;s school to review records, correct something, or manage consent to share.
        The school makes the decision; we pass your request along and show the status here.
      </p>

      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 14px", fontSize: 17, color: "#1e3a5f" }}>New request</h2>
        {students.length === 0 && !loading ? (
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            No linked children yet. Once your school links a student to your account, you can submit a request here.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle} htmlFor="rights-student">
                Child
              </label>
              <select
                id="rights-student"
                style={inputStyle}
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                required
              >
                <option value="">Select a child…</option>
                {students.map((student) => (
                  <option key={student.student_id} value={student.student_id}>
                    {student.first_name || student.last_name
                      ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
                      : student.student_name || `Student ${student.student_id}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle} htmlFor="rights-type">
                What do you need?
              </label>
              <select
                id="rights-type"
                style={inputStyle}
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="rights-detail">
                Details for the school
              </label>
              <textarea
                id="rights-detail"
                style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                value={requestDetail}
                onChange={(e) => setRequestDetail(e.target.value)}
                placeholder="Tell the school what you want to review or change. Keep it brief and specific."
              />
            </div>
            {error && (
              <div style={{ color: "#b91c1c", fontSize: 14, marginBottom: 12 }}>{error}</div>
            )}
            {success && (
              <div style={{ color: "#166534", fontSize: 14, marginBottom: 12 }}>{success}</div>
            )}
            <button
              type="submit"
              disabled={submitting || loading}
              style={{
                background: "#3256a2",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? "default" : "pointer",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Sending…" : "Send to school"}
            </button>
          </form>
        )}
      </div>

      <h2 style={{ margin: "8px 0 12px", fontSize: 17, color: "#1e3a5f" }}>Your requests</h2>
      {loading ? (
        <div style={{ color: "#64748b", fontSize: 14 }}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{ ...cardStyle, color: "#64748b", fontSize: 14 }}>
          No requests yet. When you send one, it will show up here with the school&apos;s response.
        </div>
      ) : (
        requests.map((req) => (
          <div key={req.request_id} style={cardStyle}>
            <div style={{ fontWeight: 600, color: "#1e3a5f", marginBottom: 6 }}>
              {TYPE_LABELS[req.request_type] || req.request_type}
              {req.student_name ? ` — ${req.student_name}` : ""}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>
              Submitted {formatDate(req.submitted_at)} · School response due by {formatDate(req.due_at)}
            </div>
            <div style={{ fontSize: 14, color: "#334155", marginBottom: 8 }}>
              {STATUS_LABELS[req.status] || req.status}
            </div>
            {req.request_detail && (
              <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>
                <strong>Your note:</strong> {req.request_detail}
              </div>
            )}
            {req.resolution_notes && (
              <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>
                <strong>School note:</strong> {req.resolution_notes}
              </div>
            )}
            {req.parent_statement && (
              <div style={{ fontSize: 13, color: "#475569" }}>
                <strong>Your statement on file:</strong> {req.parent_statement}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
